// src/components/UniversitySettingsCard.jsx
// 大学の設定（教務）【2026-11-04】
//   ・学籍番号の形 … 登録とログインIDの決まり（ほかの学生の番号をログインIDにさせない）・
//                    メールアドレスから学籍番号を読む・名簿や成績のファイルで学生の行を見分ける、に使う
//   ・取り込み方法 … 使わないものは取り込みの画面に出さない（サーバーでも止める）
//   GET / PUT /api/university-settings
import React, { useEffect, useState } from 'react';
import Notice, { toast } from './Notice';
import { confirmDialog } from './ConfirmDialog';
import { useUniversitySettings, setUniversitySettings } from '../hooks/useUniversitySettings';

const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token'), 'Content-Type': 'application/json' });
const KEYS = ['lettersMin', 'lettersMax', 'digitsMin', 'digitsMax'];
const reOf = (f) => {
  const n = KEYS.map(k => Number(f[k]));
  if (n.some(x => !Number.isInteger(x) || x < 0) || n[0] > n[1] || n[2] > n[3] || n[2] < 1) return null;
  return new RegExp('^' + (n[1] === 0 ? '' : `[A-Z]{${n[0]},${n[1]}}`) + `\\d{${n[2]},${n[3]}}$`);
};
const sameFormat = (a, b) => !!a && !!b && KEYS.every(k => Number(a[k]) === Number(b[k]));

export default function UniversitySettingsCard() {
  const data = useUniversitySettings();
  const [fmt, setFmt] = useState(null);
  const [srcs, setSrcs] = useState({});
  const [sample, setSample] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!data) return;
    setFmt(Object.fromEntries(KEYS.map(k => [k, String(data.studentIdFormat[k])])));
    setSrcs(Object.fromEntries(data.importSources.map(s => [s.code, s.enabled])));
    if (!sample) setSample(data.studentIdFormat.example || '');
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (data === undefined) return <div className="card"><p style={{ color: '#6b7280' }}>読み込み中…</p></div>;
  if (data === null) return <div className="card"><Notice kind="error">大学の設定を読み込めませんでした（サーバーが古い可能性があります）。</Notice></div>;
  if (!fmt) return null;

  const canEdit = data.canEdit;
  const re = reOf(fmt);
  const sampleNorm = sample.normalize('NFKC').trim().toUpperCase();
  const fmtChanged = !sameFormat(fmt, data.studentIdFormat);
  const srcChanged = data.importSources.some(s => srcs[s.code] !== s.enabled);

  const save = async (body, confirmText) => {
    if (confirmText && !(await confirmDialog(confirmText, { title: '大学の設定を変えます', okLabel: '変える', danger: false }))) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch('/api/university-settings', { method: 'PUT', headers: auth(), body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.success) throw new Error(j.message || '保存できませんでした');
      setUniversitySettings(j);
      toast.success(j.message || '保存しました');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const saveFormat = () => save(
    { studentIdFormat: Object.fromEntries(KEYS.map(k => [k, Number(fmt[k])])) },
    '学籍番号の形を変えると、新しく登録する学生のログインIDの決まりと、名簿・成績のファイルで学生の行を見分ける形が変わります。すでに登録した学生のアカウントは変わりません。');
  const resetFormat = () => save({ studentIdFormat: null }, '学籍番号の形を既定（英字1〜2文字＋数字4〜8桁）に戻します。');
  const saveSources = () => save({ importSources: srcs });

  const num = (k, min, max) => (
    <input type="number" min={min} max={max} value={fmt[k]} disabled={!canEdit || busy} style={{ width: '4.2rem' }}
           onChange={e => setFmt(f => ({ ...f, [k]: e.target.value }))} />
  );

  return (
    <>
      <div className="card">
        <div className="card-header"><h2>学籍番号の形</h2></div>
        <p style={{ fontSize: '0.88rem', color: '#4b5563', lineHeight: 1.7, marginTop: 0 }}>
          この大学の学籍番号が、英字何文字と数字何桁でできているかを決めます。次の3つに使います。
          (1) 学籍番号の形のログインIDは、本人の番号（大学のメールアドレスの @ の前）のときだけ使える。
          (2) メールアドレスから本当の学籍番号を読む。(3) 名簿や成績のファイルで、学生の行を見分ける。
        </p>
        <Notice kind="error">{err}</Notice>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
          <span>英字</span>{num('lettersMin', 0, 4)}<span>〜</span>{num('lettersMax', 0, 4)}<span>文字 ＋ 数字</span>
          {num('digitsMin', 1, 12)}<span>〜</span>{num('digitsMax', 1, 12)}<span>桁</span>
          {!data.studentIdFormat.custom && <small style={{ color: '#6b7280' }}>（いまは既定）</small>}
        </div>
        <small style={{ color: '#6b7280' }}>英字を使わない大学は、英字を 0〜0 にします。大文字・小文字は区別しません。</small>
        <div style={{ marginTop: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <label>試す：</label>
          <input value={sample} onChange={e => setSample(e.target.value)} placeholder="学籍番号を入れてみる" style={{ width: '12rem' }} />
          {re === null
            ? <span style={{ color: '#dc2626' }}>文字数・桁数の指定がおかしいです</span>
            : sampleNorm && (re.test(sampleNorm)
              ? <span style={{ color: '#15803d', fontWeight: 600 }}>学籍番号として読みます</span>
              : <span style={{ color: '#b45309', fontWeight: 600 }}>学籍番号として読みません</span>)}
        </div>
        {canEdit && (
          <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem' }}>
            <button type="button" className="btn-primary" disabled={busy || !fmtChanged || re === null} onClick={saveFormat}>保存</button>
            {data.studentIdFormat.custom && <button type="button" className="btn-secondary" disabled={busy} onClick={resetFormat}>既定に戻す</button>}
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header"><h2>取り込み方法</h2></div>
        <p style={{ fontSize: '0.88rem', color: '#4b5563', marginTop: 0 }}>
          この大学で使う取り込み方法を選びます。外したものは、先生・教務の取り込みの画面に出なくなります（取り込み済みのデータは消えません）。
        </p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {data.importSources.map(s => (
            <li key={s.code} style={{ padding: '0.45rem 0', borderBottom: '1px solid #f1f5f9' }}>
              <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', cursor: canEdit ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={!!srcs[s.code]} disabled={!canEdit || busy}
                       onChange={e => setSrcs(x => ({ ...x, [s.code]: e.target.checked }))} style={{ marginTop: '0.25rem' }} />
                <span><b>{s.name}</b><br /><small style={{ color: '#6b7280' }}>{s.description}</small></span>
              </label>
            </li>
          ))}
        </ul>
        {canEdit
          ? <div style={{ marginTop: '0.8rem' }}><button type="button" className="btn-primary" disabled={busy || !srcChanged} onClick={saveSources}>保存</button></div>
          : <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>変えられるのは教務だけです。</p>}
      </div>
    </>
  );
}
